import React, { useState, useEffect, useCallback } from 'react';
import MonitoramentoService, { OverviewMonitoramento, PanoramaData, TentativaLogin, ContaBloqueada } from '../services/monitoramentoService';
import AutoTestRunner from '../components/test/AutoTestRunner';

const MonitoramentoSistema: React.FC = () => {
  const [overview, setOverview] = useState<OverviewMonitoramento | null>(null);
  const [panorama, setPanorama] = useState<PanoramaData | null>(null);
  const [tentativasDetalhe, setTentativasDetalhe] = useState<TentativaLogin[]>([]);
  const [contasBloqueadas, setContasBloqueadas] = useState<ContaBloqueada[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [abaAtiva, setAbaAtiva] = useState<'panorama' | 'visao-geral' | 'performance' | 'acessos' | 'tentativas' | 'bloqueios' | 'seguranca' | 'logs' | 'testes'>('panorama');
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState<Date>(new Date());
  const [filtroTentativas, setFiltroTentativas] = useState<'todas' | 'falhas'>('falhas');

  const carregarDados = useCallback(async () => {
    try {
      setErro('');
      const [ov, pan] = await Promise.all([
        MonitoramentoService.buscarOverview(),
        MonitoramentoService.buscarPanorama().catch(() => null)
      ]);
      setOverview(ov);
      if (pan) setPanorama(pan);
      setUltimaAtualizacao(new Date());
    } catch (error: any) {
      console.error('Erro ao carregar monitoramento:', error);
      setErro(error.message || 'Erro ao carregar dados de monitoramento');
    } finally {
      setLoading(false);
    }
  }, []);

  const carregarTentativas = useCallback(async () => {
    try {
      const data = await MonitoramentoService.buscarTentativasLogin(filtroTentativas === 'falhas', 100);
      setTentativasDetalhe(data);
    } catch (error) {
      console.error('Erro ao carregar tentativas:', error);
    }
  }, [filtroTentativas]);

  const carregarBloqueios = useCallback(async () => {
    try {
      const data = await MonitoramentoService.buscarContasBloqueadas();
      setContasBloqueadas(data);
    } catch (error) {
      console.error('Erro ao carregar bloqueios:', error);
    }
  }, []);

  useEffect(() => {
    carregarDados();
    const interval = setInterval(() => {
      if (document.hidden) return; // Não consultar com a aba oculta (reduz IO no banco)
      carregarDados();
    }, 300000); // 5 minutos
    return () => clearInterval(interval);
  }, [carregarDados]);

  useEffect(() => {
    if (abaAtiva === 'tentativas') carregarTentativas();
    if (abaAtiva === 'bloqueios') carregarBloqueios();
  }, [abaAtiva, carregarTentativas, carregarBloqueios]);

  const handleDesbloquear = async (usuarioId: string, nome: string) => {
    if (!window.confirm(`Confirma o desbloqueio da conta de ${nome}?`)) return;
    try {
      await MonitoramentoService.desbloquearConta(usuarioId);
      alert('Conta desbloqueada com sucesso.');
      carregarBloqueios();
      carregarDados();
    } catch (error: any) {
      alert(`Erro ao desbloquear: ${error.message}`);
    }
  };

  const formatarTempoRelativo = (dataISO: string): string => {
    const diff = Date.now() - new Date(dataISO).getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'agora mesmo';
    if (min < 60) return `há ${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `há ${h}h`;
    const d = Math.floor(h / 24);
    return `há ${d}d`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'text-green-700 bg-green-100 border-green-200';
      case 'offline': return 'text-red-700 bg-red-100 border-red-200';
      default: return 'text-gray-700 bg-gray-100 border-gray-200';
    }
  };

  const getAcaoCor = (acao: string): string => {
    switch (acao) {
      case 'LOGIN': return 'text-green-400';
      case 'LOGOUT': return 'text-red-400';
      case 'CREATE': return 'text-blue-400';
      case 'UPDATE': return 'text-yellow-400';
      case 'DELETE': return 'text-red-400';
      case 'VIEW': return 'text-purple-400';
      default: return 'text-gray-400';
    }
  };

  if (loading && !overview) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (erro && !overview) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-700 font-semibold">Erro ao carregar monitoramento</p>
          <p className="text-red-600 text-sm mt-2">{erro}</p>
          <button onClick={carregarDados} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  const abas = [
    { key: 'panorama', label: 'Panorama' },
    { key: 'visao-geral', label: 'Visão Geral' },
    { key: 'performance', label: 'Performance' },
    { key: 'acessos', label: 'Acessos & Sessões' },
    { key: 'tentativas', label: 'Tentativas de Login' },
    { key: 'bloqueios', label: 'Contas Bloqueadas' },
    { key: 'seguranca', label: 'Segurança' },
    { key: 'logs', label: 'Logs do Sistema' },
    { key: 'testes', label: 'Test Runner' },
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Monitoramento do Sistema</h1>
          <p className="text-sm text-gray-500 mt-1">
            Última atualização: {ultimaAtualizacao.toLocaleTimeString('pt-BR')}
            <button onClick={carregarDados} className="ml-3 text-blue-600 hover:text-blue-800 text-xs underline">
              Atualizar agora
            </button>
          </p>
        </div>
      </div>

      {/* Abas */}
      <div className="flex space-x-1 bg-white rounded-lg shadow p-1 mb-6 overflow-x-auto">
        {abas.map(aba => (
          <button
            key={aba.key}
            onClick={() => setAbaAtiva(aba.key as any)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              abaAtiva === aba.key
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            {aba.label}
            {aba.key === 'bloqueios' && overview && overview.total_contas_bloqueadas > 0 && (
              <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                {overview.total_contas_bloqueadas}
              </span>
            )}
            {aba.key === 'tentativas' && overview && overview.total_tentativas_falhas_24h > 0 && (
              <span className="ml-2 bg-orange-500 text-white text-xs rounded-full px-2 py-0.5">
                {overview.total_tentativas_falhas_24h}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* === PANORAMA === */}
      {abaAtiva === 'panorama' && panorama && (
        <div className="space-y-6">
          {/* Performance rápida */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Latência API/Banco</h3>
              <p className="text-3xl font-bold text-blue-600">{panorama.performance.api_latency_ms}<span className="text-lg text-gray-400">ms</span></p>
              <p className="text-xs text-gray-400 mt-1">Status: {panorama.performance.status_banco}</p>
            </div>
            <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Uptime do Node</h3>
              <p className="text-3xl font-bold text-green-600">{panorama.performance.node_uptime_horas}h{panorama.performance.node_uptime_minutos}m</p>
              <p className="text-xs text-gray-400 mt-1">Node {panorama.performance.node_version}</p>
            </div>
            <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Memória (RSS)</h3>
              <p className="text-3xl font-bold text-purple-600">{panorama.performance.memoria_mb}<span className="text-lg text-gray-400">MB</span></p>
              <p className="text-xs text-gray-400 mt-1">Consumo do processo</p>
            </div>
            <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Filiais Ativas</h3>
              <p className="text-3xl font-bold text-orange-600">{panorama.filiais_ativas}</p>
              <p className="text-xs text-gray-400 mt-1">Unidades operacionais</p>
            </div>
          </div>

          {/* Solicitações de Agendamento */}
          <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Solicitações de Agendamento</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="text-center p-3 bg-yellow-50 rounded-lg">
                <p className="text-2xl font-bold text-yellow-600">{panorama.solicitacoes.pendentes}</p>
                <p className="text-xs text-gray-500 mt-1">Pendentes</p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{panorama.solicitacoes.aprovadas}</p>
                <p className="text-xs text-gray-500 mt-1">Aprovadas</p>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <p className="text-2xl font-bold text-red-600">{panorama.solicitacoes.reprovadas}</p>
                <p className="text-xs text-gray-500 mt-1">Reprovadas</p>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">{panorama.solicitacoes.reagendadas}</p>
                <p className="text-xs text-gray-500 mt-1">Reagendadas</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-700">{panorama.solicitacoes.total}</p>
                <p className="text-xs text-gray-500 mt-1">Total</p>
              </div>
            </div>
          </div>

          {/* Cargas e Visitas lado a lado */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Cargas</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-xl font-bold text-blue-600">{panorama.cargas.em_andamento}</p>
                  <p className="text-xs text-gray-500 mt-1">Em Andamento</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-xl font-bold text-green-600">{panorama.cargas.finalizadas}</p>
                  <p className="text-xs text-gray-500 mt-1">Finalizadas</p>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <p className="text-xl font-bold text-red-600">{panorama.cargas.canceladas}</p>
                  <p className="text-xs text-gray-500 mt-1">Canceladas</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xl font-bold text-gray-700">{panorama.cargas.total}</p>
                  <p className="text-xs text-gray-500 mt-1">Total</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Visitas</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="text-center p-3 bg-yellow-50 rounded-lg">
                  <p className="text-xl font-bold text-yellow-600">{panorama.visitas.pendentes}</p>
                  <p className="text-xs text-gray-500 mt-1">Pendentes</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-xl font-bold text-green-600">{panorama.visitas.aprovadas}</p>
                  <p className="text-xs text-gray-500 mt-1">Aprovadas</p>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-xl font-bold text-blue-600">{panorama.visitas.finalizadas}</p>
                  <p className="text-xs text-gray-500 mt-1">Finalizadas</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xl font-bold text-gray-700">{panorama.visitas.total}</p>
                  <p className="text-xs text-gray-500 mt-1">Total</p>
                </div>
              </div>
            </div>
          </div>

          {/* Documentos, Notificações, Pedidos */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Documentos & Uploads</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm"><span className="text-gray-600">Total documentos:</span><span className="font-semibold text-gray-900">{panorama.documentos.total}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-600">Uploads 24h:</span><span className="font-semibold text-blue-600">{panorama.documentos.recentes_24h}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-600">Uploads sucesso 7d:</span><span className="font-semibold text-green-600">{panorama.documentos.uploads_sucesso_7d}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-600">Uploads falha 7d:</span><span className="font-semibold text-red-600">{panorama.documentos.uploads_falha_7d}</span></div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Notificações</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm"><span className="text-gray-600">Total:</span><span className="font-semibold text-gray-900">{panorama.notificacoes.total}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-600">Não lidas:</span><span className="font-semibold text-orange-600">{panorama.notificacoes.nao_lidas}</span></div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Pedidos</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm"><span className="text-gray-600">Total:</span><span className="font-semibold text-gray-900">{panorama.pedidos.total}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-600">Criados 7d:</span><span className="font-semibold text-blue-600">{panorama.pedidos.recentes_7d}</span></div>
              </div>
            </div>
          </div>

          {/* Segurança resumido */}
          <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Segurança (resumo)</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <p className="text-xl font-bold text-purple-600">{panorama.seguranca.captchas_gerados_24h}</p>
                <p className="text-xs text-gray-500 mt-1">CAPTCHAs 24h</p>
              </div>
              <div className="text-center p-3 bg-yellow-50 rounded-lg">
                <p className="text-xl font-bold text-yellow-600">{panorama.seguranca.solicitacoes_acesso_pendentes}</p>
                <p className="text-xs text-gray-500 mt-1">Acessos Pendentes</p>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <p className="text-xl font-bold text-red-600">{panorama.seguranca.logs_erro_7d}</p>
                <p className="text-xs text-gray-500 mt-1">Erros 7d</p>
              </div>
              <div className="text-center p-3 bg-orange-50 rounded-lg">
                <p className="text-xl font-bold text-orange-600">{panorama.seguranca.delecoes_7d}</p>
                <p className="text-xs text-gray-500 mt-1">Deleções 7d</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* === PERFORMANCE === */}
      {abaAtiva === 'performance' && panorama && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700">Latência API</h3>
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(panorama.performance.api_latency_ms < 500 ? 'online' : 'offline')}`}>
                  {panorama.performance.api_latency_ms < 500 ? 'RAPIDO' : 'LENTO'}
                </span>
              </div>
              <p className="text-3xl font-bold text-gray-900">{panorama.performance.api_latency_ms}<span className="text-lg text-gray-400">ms</span></p>
              <p className="text-xs text-gray-400 mt-1">Tempo de resposta do banco</p>
            </div>
            <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Uptime Node.js</h3>
              <p className="text-3xl font-bold text-green-600">{panorama.performance.node_uptime_horas}h {panorama.performance.node_uptime_minutos}m</p>
              <p className="text-xs text-gray-400 mt-1">Tempo do processo ativo</p>
            </div>
            <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Memória RSS</h3>
              <p className="text-3xl font-bold text-purple-600">{panorama.performance.memoria_mb}<span className="text-lg text-gray-400">MB</span></p>
              <p className="text-xs text-gray-400 mt-1">Resident Set Size do Node</p>
            </div>
            <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Versão Node</h3>
              <p className="text-3xl font-bold text-gray-900">{panorama.performance.node_version}</p>
              <p className="text-xs text-gray-400 mt-1">Runtime do backend</p>
            </div>
          </div>

          {/* Status do banco detalhado */}
          <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Saúde do Banco de Dados</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-600">Status da conexão</span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${panorama.performance.status_banco === 'online' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {panorama.performance.status_banco === 'online' ? 'ONLINE' : 'DEGRADADO'}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-600">Latência</span>
                <span className={`text-sm font-semibold ${panorama.performance.latencia_banco_ms < 200 ? 'text-green-600' : panorama.performance.latencia_banco_ms < 1000 ? 'text-yellow-600' : 'text-red-600'}`}>
                  {panorama.performance.latencia_banco_ms}ms
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-600">Avaliacao</span>
                <span className="text-sm font-semibold text-gray-700">
                  {panorama.performance.latencia_banco_ms < 100 ? 'Excelente' : panorama.performance.latencia_banco_ms < 300 ? 'Boa' : panorama.performance.latencia_banco_ms < 1000 ? 'Aceitavel' : 'Lenta - verificar'}
                </span>
              </div>
            </div>
          </div>

          {/* Uploads recentes */}
          <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Uploads Recentes (7 dias)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <p className="text-3xl font-bold text-green-600">{panorama.documentos.uploads_sucesso_7d}</p>
                <p className="text-sm text-gray-600 mt-1">Uploads bem-sucedidos</p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg text-center">
                <p className="text-3xl font-bold text-red-600">{panorama.documentos.uploads_falha_7d}</p>
                <p className="text-sm text-gray-600 mt-1">Uploads com falha</p>
              </div>
            </div>
            {panorama.documentos.uploads_recentes.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Data</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Usuario</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Acao</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Descricao</th>
                    </tr>
                  </thead>
                  <tbody>
                    {panorama.documentos.uploads_recentes.map((log) => (
                      <tr key={log.id} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-2 px-3 text-gray-600 text-xs">{new Date(log.data_acao).toLocaleString('pt-BR')}</td>
                        <td className="py-2 px-3 text-gray-700 font-medium">{log.usuario_nome}</td>
                        <td className="py-2 px-3"><span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700">{log.acao}</span></td>
                        <td className="py-2 px-3 text-gray-600 text-xs max-w-xs truncate">{log.descricao}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* === VISÃO GERAL === */}
      {abaAtiva === 'visao-geral' && overview && (
        <div className="space-y-6">
          {/* Cards de status */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Banco de Dados */}
            <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700">Banco de Dados</h3>
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(overview.status_banco)}`}>
                  {overview.status_banco.toUpperCase()}
                </span>
              </div>
              <div className="text-sm text-gray-600">
                <p>Latência: <span className="font-semibold text-gray-900">{overview.latencia_banco}ms</span></p>
                <p className="mt-1 text-xs text-gray-400">Verificado em tempo real</p>
              </div>
            </div>

            {/* Usuários Online */}
            <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700">Usuários Online</h3>
                <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                  ATIVO
                </span>
              </div>
              <div className="text-sm text-gray-600">
                <p className="text-3xl font-bold text-gray-900">{overview.total_online}</p>
                <p className="mt-1 text-xs text-gray-400">Sessões ativas nos últimos 30 min</p>
              </div>
            </div>

            {/* Tentativas Falhas */}
            <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700">Tentativas Falhas</h3>
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${
                  overview.total_tentativas_falhas_24h > 5
                    ? 'bg-red-100 text-red-700 border-red-200'
                    : overview.total_tentativas_falhas_24h > 0
                    ? 'bg-yellow-100 text-yellow-700 border-yellow-200'
                    : 'bg-green-100 text-green-700 border-green-200'
                }`}>
                  24H
                </span>
              </div>
              <div className="text-sm text-gray-600">
                <p className="text-3xl font-bold text-gray-900">{overview.total_tentativas_falhas_24h}</p>
                <p className="mt-1 text-xs text-gray-400">{overview.tentativas_falhas_1h} na última hora</p>
              </div>
            </div>

            {/* Contas Bloqueadas */}
            <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700">Contas Bloqueadas</h3>
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${
                  overview.total_contas_bloqueadas > 0
                    ? 'bg-red-100 text-red-700 border-red-200'
                    : 'bg-green-100 text-green-700 border-green-200'
                }`}>
                  {overview.total_contas_bloqueadas > 0 ? 'ALERTA' : 'OK'}
                </span>
              </div>
              <div className="text-sm text-gray-600">
                <p className="text-3xl font-bold text-gray-900">{overview.total_contas_bloqueadas}</p>
                <p className="mt-1 text-xs text-gray-400">Contas com acesso bloqueado</p>
              </div>
            </div>
          </div>

          {/* Resumo de usuários */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Total de Usuários</h3>
              <p className="text-3xl font-bold text-blue-600">{overview.total_usuarios}</p>
              <p className="text-xs text-gray-400 mt-1">Cadastrados no sistema</p>
            </div>
            <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Usuários Ativos</h3>
              <p className="text-3xl font-bold text-green-600">{overview.usuarios_ativos}</p>
              <p className="text-xs text-gray-400 mt-1">Com acesso habilitado</p>
            </div>
            <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Usuários Inativos</h3>
              <p className="text-3xl font-bold text-gray-500">{overview.usuarios_inativos}</p>
              <p className="text-xs text-gray-400 mt-1">Com acesso desabilitado</p>
            </div>
          </div>

          {/* Logins por IP (tentativas falhas) */}
          {Object.keys(overview.tentativas_por_ip).length > 0 && (
            <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Tentativas Falhas por IP (24h)</h3>
              <div className="space-y-2">
                {Object.entries(overview.tentativas_por_ip)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 10)
                  .map(([ip, count]) => (
                    <div key={ip} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                      <span className="text-sm font-mono text-gray-700">{ip}</span>
                      <span className={`text-sm font-semibold ${count >= 5 ? 'text-red-600' : count >= 3 ? 'text-orange-600' : 'text-gray-600'}`}>
                        {count} tentativa{count !== 1 ? 's' : ''}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Usuários online agora */}
          <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Usuários Online Agora</h3>
            {overview.usuarios_online.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">Nenhum usuário online no momento</p>
            ) : (
              <div className="space-y-2">
                {overview.usuarios_online.map((sessao) => (
                  <div key={sessao.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{sessao.usuario_nome}</p>
                        <p className="text-xs text-gray-500">{sessao.usuario_login}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-600">{formatarTempoRelativo(sessao.ultima_atividade)}</p>
                      <p className="text-xs text-gray-400">{sessao.filial_atual || 'Sem filial'}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* === ACESSOS & SESSÕES === */}
      {abaAtiva === 'acessos' && overview && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Logins Bem-sucedidos (24h)</h3>
              <p className="text-3xl font-bold text-green-600">{overview.tentativas_sucesso_24h}</p>
            </div>
            <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Logins Falhados (24h)</h3>
              <p className="text-3xl font-bold text-red-600">{overview.total_tentativas_falhas_24h}</p>
            </div>
            <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Online Agora</h3>
              <p className="text-3xl font-bold text-blue-600">{overview.total_online}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Sessões Ativas</h3>
            {overview.usuarios_online.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">Nenhuma sessão ativa</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Usuário</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Login</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Filial</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">IP</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Última Atividade</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Navegador</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.usuarios_online.map((sessao) => (
                      <tr key={sessao.id} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-2 px-3">
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                            <span className="font-medium text-gray-900">{sessao.usuario_nome}</span>
                          </div>
                        </td>
                        <td className="py-2 px-3 text-gray-600">{sessao.usuario_login}</td>
                        <td className="py-2 px-3 text-gray-600">{sessao.filial_atual || 'N/A'}</td>
                        <td className="py-2 px-3 text-gray-500 font-mono text-xs">{sessao.ip_address || 'N/A'}</td>
                        <td className="py-2 px-3 text-gray-600">{formatarTempoRelativo(sessao.ultima_atividade)}</td>
                        <td className="py-2 px-3 text-gray-400 text-xs max-w-xs truncate">{sessao.user_agent || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* === TENTATIVAS DE LOGIN === */}
      {abaAtiva === 'tentativas' && (
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setFiltroTentativas('falhas')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${filtroTentativas === 'falhas' ? 'bg-red-600 text-white' : 'bg-white text-gray-600 border border-gray-200'}`}
            >
              Apenas Falhas
            </button>
            <button
              onClick={() => setFiltroTentativas('todas')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${filtroTentativas === 'todas' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-200'}`}
            >
              Todas
            </button>
          </div>

          <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">
              Tentativas de Login {filtroTentativas === 'falhas' ? 'Falhas' : 'Recentes'}
            </h3>
            {tentativasDetalhe.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">Nenhuma tentativa encontrada</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Data/Hora</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Usuário</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">IP</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Navegador</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Resultado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tentativasDetalhe.map((t) => (
                      <tr key={t.id} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-2 px-3 text-gray-600">{new Date(t.criado_em).toLocaleString('pt-BR')}</td>
                        <td className="py-2 px-3">
                          {t.usuarios ? (
                            <div>
                              <p className="font-medium text-gray-900">{t.usuarios.nome}</p>
                              <p className="text-xs text-gray-500">{t.usuarios.login}</p>
                            </div>
                          ) : (
                            <span className="text-gray-400 italic">Usuário não encontrado</span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-gray-500 font-mono text-xs">{t.ip_address || 'N/A'}</td>
                        <td className="py-2 px-3 text-gray-400 text-xs max-w-xs truncate">{t.user_agent || 'N/A'}</td>
                        <td className="py-2 px-3">
                          {t.sucesso ? (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Sucesso</span>
                          ) : (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">Falha</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* === CONTAS BLOQUEADAS === */}
      {abaAtiva === 'bloqueios' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Contas Bloqueadas</h3>
            {contasBloqueadas.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-green-600 font-medium">Nenhuma conta bloqueada</p>
                <p className="text-sm text-gray-400 mt-1">Todos os usuários estão liberados</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Usuário</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Motivo</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Tipo</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Bloqueado em</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contasBloqueadas.map((c) => (
                      <tr key={c.id} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-2 px-3">
                          {c.usuarios ? (
                            <div>
                              <p className="font-medium text-gray-900">{c.usuarios.nome}</p>
                              <p className="text-xs text-gray-500">{c.usuarios.login}</p>
                            </div>
                          ) : (
                            <span className="text-gray-400 italic">ID: {c.usuario_id}</span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-gray-600 max-w-xs">{c.motivo}</td>
                        <td className="py-2 px-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            c.tipo_bloqueio === 'permanente'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {c.tipo_bloqueio === 'permanente' ? 'Permanente' : 'Temporário'}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-gray-600">{new Date(c.bloqueado_em).toLocaleString('pt-BR')}</td>
                        <td className="py-2 px-3">
                          <button
                            onClick={() => handleDesbloquear(c.usuario_id, c.usuarios?.nome || c.usuario_id)}
                            className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs font-medium"
                          >
                            Desbloquear
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* === LOGS DO SISTEMA === */}
      {abaAtiva === 'logs' && overview && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Logs de Atividades Recentes</h3>
            <div className="bg-gray-900 rounded-lg p-4 h-[500px] overflow-y-auto font-mono text-sm">
              {overview.logs_recentes.length === 0 ? (
                <div className="text-gray-500 text-center py-8">Nenhum log disponível</div>
              ) : (
                overview.logs_recentes.map((log) => (
                  <div key={log.id} className="mb-2">
                    <span className="text-gray-400">[{new Date(log.data_acao).toLocaleString('pt-BR')}]</span>
                    <span className={`ml-2 ${getAcaoCor(log.acao)}`}>[{log.acao}]</span>
                    <span className="ml-2 text-cyan-400">[{log.modulo}]</span>
                    <span className="ml-2 text-gray-300">{log.usuario_nome} ({log.usuario_login})</span>
                    <span className="ml-2 text-gray-400">- {log.descricao}</span>
                    {log.filial_atual && (
                      <span className="ml-2 text-gray-500 text-xs">[{log.filial_atual}]</span>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* === SEGURANCA === */}
      {abaAtiva === 'seguranca' && panorama && (
        <div className="space-y-6">
          {/* Cards de seguranca */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">CAPTCHAs Gerados (24h)</h3>
              <p className="text-3xl font-bold text-purple-600">{panorama.seguranca.captchas_gerados_24h}</p>
              <p className="text-xs text-gray-400 mt-1">Protecao anti-bot ativa</p>
            </div>
            <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Solic. Acesso Pendentes</h3>
              <p className="text-3xl font-bold text-yellow-600">{panorama.seguranca.solicitacoes_acesso_pendentes}</p>
              <p className="text-xs text-gray-400 mt-1">Pedidos de cadastro no sistema</p>
            </div>
            <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Erros Registrados (7d)</h3>
              <p className="text-3xl font-bold text-red-600">{panorama.seguranca.logs_erro_7d}</p>
              <p className="text-xs text-gray-400 mt-1">Logs com erro/falha nos ultimos 7 dias</p>
            </div>
            <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Delecoes (7d)</h3>
              <p className="text-3xl font-bold text-orange-600">{panorama.seguranca.delecoes_7d}</p>
              <p className="text-xs text-gray-400 mt-1">Exclusoes de arquivos/cargas/dados</p>
            </div>
          </div>

          {/* Tentativas de ataque (baseado em CAPTCHAs e IPs) */}
          {overview && (
            <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Tentativas Suspeitas (24h)</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="p-4 bg-red-50 rounded-lg text-center">
                  <p className="text-3xl font-bold text-red-600">{overview.total_tentativas_falhas_24h}</p>
                  <p className="text-sm text-gray-600 mt-1">Logins falhados (24h)</p>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg text-center">
                  <p className="text-3xl font-bold text-orange-600">{overview.tentativas_falhas_1h}</p>
                  <p className="text-sm text-gray-600 mt-1">Logins falhados (1h)</p>
                </div>
                <div className="p-4 bg-gray-100 rounded-lg text-center">
                  <p className="text-3xl font-bold text-gray-700">{overview.total_contas_bloqueadas}</p>
                  <p className="text-sm text-gray-600 mt-1">Contas bloqueadas agora</p>
                </div>
              </div>
              {Object.keys(overview.tentativas_por_ip).length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">IPs com tentativas falhas (possivel ataque brute-force):</p>
                  <div className="space-y-2">
                    {Object.entries(overview.tentativas_por_ip)
                      .sort(([, a], [, b]) => b - a)
                      .slice(0, 15)
                      .map(([ip, count]) => (
                        <div key={ip} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                          <span className="text-sm font-mono text-gray-700">{ip}</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${count >= 5 ? 'bg-red-100 text-red-700' : count >= 3 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'}`}>
                            {count} tentativa{count !== 1 ? 's' : ''}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Logs de erro detalhados */}
          <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Logs de Erro (7 dias)</h3>
            {panorama.seguranca.logs_erro_detalhes.length === 0 ? (
              <p className="text-sm text-green-600 text-center py-4">Nenhum erro registrado nos ultimos 7 dias</p>
            ) : (
              <div className="bg-gray-900 rounded-lg p-4 h-64 overflow-y-auto font-mono text-sm">
                {panorama.seguranca.logs_erro_detalhes.map((log) => (
                  <div key={log.id} className="mb-2">
                    <span className="text-gray-400">[{new Date(log.data_acao).toLocaleString('pt-BR')}]</span>
                    <span className="ml-2 text-red-400">[{log.acao}]</span>
                    <span className="ml-2 text-cyan-400">[{log.modulo}]</span>
                    <span className="ml-2 text-gray-300">{log.usuario_nome}</span>
                    <span className="ml-2 text-gray-400">- {log.descricao}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Delecoes recentes */}
          <div className="bg-white rounded-xl shadow p-5 border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Delecoes Recentes (7 dias)</h3>
            {panorama.seguranca.delecoes_detalhes.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">Nenhuma delecao registrada</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Data</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Usuario</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Acao</th>
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Descricao</th>
                    </tr>
                  </thead>
                  <tbody>
                    {panorama.seguranca.delecoes_detalhes.map((log) => (
                      <tr key={log.id} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-2 px-3 text-gray-600 text-xs">{new Date(log.data_acao).toLocaleString('pt-BR')}</td>
                        <td className="py-2 px-3 text-gray-700 font-medium">{log.usuario_nome}</td>
                        <td className="py-2 px-3"><span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-700">{log.acao}</span></td>
                        <td className="py-2 px-3 text-gray-600 text-xs max-w-xs truncate">{log.descricao}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* === TEST RUNNER === */}
      {abaAtiva === 'testes' && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <AutoTestRunner />
        </div>
      )}
    </div>
  );
};

export default MonitoramentoSistema;
